import React, { forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { GridPanelWrapper, useGridApiContext } from "@mui/x-data-grid-pro";

import { cloneObject } from "shared/utils";

import Button from "features/components/Button";
import { DEFAULT_FILTER_TYPE } from "features/ui/Filters/constants";
import { DEFAULT_FILTER_BUILDER_STATE } from "features/ui/Filters/FilterBuilder/constants";
import FilterBuilder from "features/ui/Filters/FilterBuilder/FilterBuilder";
import { FilterGroupState } from "features/ui/Filters/FilterBuilder/types";
import { isFilterBuilderStateValid } from "features/ui/Filters/FilterBuilder/utils";
import { getFirstAvailableSelectedOperator } from "features/ui/Filters/utils";
import { SchemaEntry } from "features/ui/MaterialTable";

declare module "@mui/x-data-grid" {
  interface FilterPanelPropsOverrides {
    filterGroupState?: FilterGroupState;
    filtersSchema?: SchemaEntry[];
    defaultFilters?: FilterGroupState;
    onApplyFilters?: (state: FilterGroupState) => void;
  }
  // Allow forwarding Popper.js options (e.g. strategy: "fixed") through the
  // basePopper slot so the filter panel tracks the sticky column-header anchor.
  interface BasePopperPropsOverrides {
    popperOptions?: Record<string, unknown>;
  }
}

interface FilterBuilderFilterPanelProps {
  filterGroupState?: FilterGroupState;
  filtersSchema?: SchemaEntry[];
  defaultFilters?: FilterGroupState;
  onApplyFilters?: (state: FilterGroupState) => void;
}

const FilterBuilderFilterPanel = forwardRef<
  HTMLDivElement,
  FilterBuilderFilterPanelProps
>(function FilterBuilderFilterPanel(props, ref) {
  const {
    filterGroupState,
    filtersSchema = [],
    defaultFilters,
    onApplyFilters,
  } = props;
  const { t } = useTranslation("filters");
  const apiRef = useGridApiContext();

  // Read and consume the column filter field set by CustomColumnMenuFilterItem.
  // useRef so the value survives React Strict Mode double-rendering.
  const columnFilterFieldRef = React.useRef(
    (apiRef.current as any).__columnFilterField as string | undefined
  );
  delete (apiRef.current as any).__columnFilterField;

  const buildState = React.useCallback((): FilterGroupState => {
    const existing =
      cloneObject(filterGroupState) ?? DEFAULT_FILTER_BUILDER_STATE;
    const targetField = columnFilterFieldRef.current;

    if (targetField) {
      const schemaEntry = filtersSchema.find((s) => s.accessor === targetField);
      if (schemaEntry) {
        const filterType =
          schemaEntry.filter?.filterType ?? DEFAULT_FILTER_TYPE;
        const defaultOperator = getFirstAvailableSelectedOperator(
          t,
          null,
          filterType,
          schemaEntry
        );
        const newRow = {
          type: "row" as const,
          id: `row-${existing.children.length}`,
          attribute: targetField,
          operator: defaultOperator,
          values: [] as string[],
        };

        return {
          ...existing,
          children: [...existing.children, newRow],
        };
      }
    }

    if (existing.children.length === 0) {
      return {
        ...existing,
        children: [
          {
            type: "row" as const,
            id: "row-0",
            attribute: null,
            operator: null,
            values: [],
          },
        ],
      };
    }

    return existing;
  }, [filterGroupState, filtersSchema, t]);

  const [innerFilterState, setInnerFilterState] = React.useState(buildState);
  const [hasChanges, setHasChanges] = React.useState(false);

  const onChangeInner = (state: FilterGroupState) => {
    setInnerFilterState(state);
    setHasChanges(true);
  };

  const handleApply = () => {
    onApplyFilters?.(innerFilterState);
    setHasChanges(false);
    apiRef.current.hideFilterPanel();
  };

  const handleCancel = () => {
    setInnerFilterState(
      cloneObject(filterGroupState) ?? DEFAULT_FILTER_BUILDER_STATE
    );
    setHasChanges(false);
    apiRef.current.hideFilterPanel();
  };

  const handleClear = () => {
    onApplyFilters?.(DEFAULT_FILTER_BUILDER_STATE);
    setHasChanges(false);
    apiRef.current.hideFilterPanel();
  };

  return (
    <GridPanelWrapper ref={ref}>
      <div className="min-w-[40rem] p-3">
        <FilterBuilder
          inline={true}
          embedded={false}
          filtersSchema={filtersSchema}
          onChange={onChangeInner}
          filterBuilderState={innerFilterState}
          defaultFilters={defaultFilters}
          hasTopLevelActions={false}
          hasInitialFocus={true}
          disableGroups={true}
          testId="table-filter-builder"
        />

        <div className="flex flex-row gap-2 justify-end mt-2">
          <Button
            testId="table-filter-clear"
            color="secondary"
            size="small"
            onClick={handleClear}
          >
            <span className="text-xs">{t("actions.clear")}</span>
          </Button>
          <Button
            testId="table-filter-cancel"
            color="secondary"
            size="small"
            onClick={handleCancel}
          >
            <span className="text-xs">{t("common:cancel")}</span>
          </Button>
          <Button
            testId="table-filter-apply"
            color="primary"
            variant="contained"
            className="h-7"
            size="small"
            onClick={handleApply}
            disabled={
              !hasChanges || !isFilterBuilderStateValid(innerFilterState)
            }
          >
            <span className="text-xs">{t("common:apply")}</span>
          </Button>
        </div>
      </div>
    </GridPanelWrapper>
  );
});

export default FilterBuilderFilterPanel;
